import React, { useEffect, useState, useCallback } from 'react'
import { Link, Redirect } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle'
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes'
import { Helmet } from 'react-helmet'
import { useAuth } from '@peracto/peracto-auth'
import { GET_LIST, GET_ONE, useClient } from '@peracto/client'
import { toast } from 'react-toastify'
import axios from 'axios'

import { Heading, Modal, Group, LoadingIcon, StyledSelect } from '@peracto/peracto-ui'
import { useConfig } from '@peracto/peracto-config'

import * as ls from 'local-storage'
import * as S from '../styled'

import OrdersCreateModal from './OrdersCreateModal'
import PromotionCodes from './PromotionCodes'
import { default as AddressModal, emptyAddress } from './AddressModal'
import CustomerModal from './CustomerModal'
import ProductList from './ProductList'

import * as basket from './basket-utils'

const currencySymbols = {
    GBP: '£',
    USD: '$',
    EUR: '€',
}

const defaultPaymentMethods = [
    {
        label: 'Pay on Account',
        value: 'payOnAccount',
    },
]

export const formatAsCurrency = (price = 0, currency = 'GBP') => {
    return `${currencySymbols[currency]}${(price / 100).toFixed(2)}`
}

const OrdersCreateContainer = ({ children }) => {
    return (
        <div className="form-container">
            <Heading name="Create Order">
                <div className="!grow !flex !items-center !justify-end">
                    <Link className="btn btn-outline-primary" to="/orders">
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                        Back to Orders
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    )
}

export const OrderAddress = ({ address, title = '' }) => (
    <>
        {title && <p className="!font-bold">{title}</p>}
        <S.OrderAddress>
            {(address?.forename || address?.surname || address?.firstName || address?.lastName) && (
                <p>
                    {address?.forename || address?.firstName}{' '}
                    {address?.surname || address?.lastName}
                </p>
            )}
            {(address?.line1 || address?.addressLine1) && (
                <p>{address?.line1 || address?.addressLine1}</p>
            )}
            {(address?.line2 || address?.addressLine2) && (
                <p>{address?.line2 || address?.addressLine2}</p>
            )}
            {(address?.line3 || address?.addressLine3) && (
                <p>{address?.line3 || address?.addressLine3}</p>
            )}
            {address?.line4 && <p>{address?.line4}</p>}
            {address?.postalCode && <p>{address?.postalCode}</p>}
            {address?.county && <p>{address?.county}</p>}
            {address?.country && <p>{address?.country}</p>}
            {address?.telephone && <p>{address?.telephone}</p>}
        </S.OrderAddress>
    </>
)

const OrdersCreate = () => {
    const [redirect, setRedirect] = useState()
    const [isLoading, setIsLoading] = useState(true)
    const [orderData, setOrderData] = useState({})
    const [userData, setUserData] = useState({})
    const [shippingServiceOptions, setShippingServiceOptions] = useState([])
    const [searchProduct, setSearchProduct] = useState('')
    const [searchSku, setSearchSku] = useState('')
    const [showProductModal, setShowProductModal] = useState(false)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [addressModal, setAddressModal] = useState(false)
    const [customerModal, setCustomerModal] = useState(false)
    const [basketErrors, setBasketErrors] = useState([])
    const { user } = useAuth()

    const config = useConfig()
    const { orders } = config.get('features', {})
    const API_URL = config.get('api')
    const paymentProviders = config.get('paymentProviders')
    const token = ls.get('token')

    const { client } = useClient()

    const paymentMethods = paymentProviders || defaultPaymentMethods

    const basketToken = ls.get(`${user?.profile?.id}_basket`)

    // show prices as tax feature flag - default: true
    const includeTax =
        orders && orders.hasOwnProperty('includeTax') ? orders.includeTax === true : true

    const getUserData = async data => {
        // user can be an object or an IRI string
        if (data?.user?.hasOwnProperty('@id') || typeof data?.user === 'string') {
            const { data: userData } = await client(GET_ONE, 'users', {
                id: data.user['@id'] || data?.user,
            })

            setUserData(userData)
        } else {
            setUserData({})
        }
    }

    const fetchData = async () => {
        let token = ls.get(`${user?.profile?.id}_basket`)

        if (!token) {
            const data = await basket.addBasket({ user })
            token = data.id
        }

        try {
            const { data } = await basket.getBasket({ id: token })

            await getUserData(data)
            setOrderData(data)
            setIsLoading(false)
        } catch (e) {
            console.log('error', e)
            setRedirect('/orders')

            if (e?.response) {
                const { data } = e.response
                console.error(e)
                toast.error(
                    data?.hasOwnProperty('hydra:description')
                        ? data['hydra:description']
                        : 'Whoops, there was a problem...'
                )
            }
        }
    }

    const onAddProducts = async product => {
        try {
            const data = await basket.updateBasket({ adminUser: user, products: [product] })
            setOrderData(data)
            setShowProductModal(false)

            toast.success(`Product was added to this order`)
        } catch (e) {
            if (e?.response) {
                const { data } = e.response
                console.error(e)
                toast.error(
                    data?.hasOwnProperty('hydra:description')
                        ? data['hydra:description']
                        : 'Whoops, there was a problem...'
                )
            }
        }
    }

    const onUpdateProduct = async product => {
        const updatedProduct = { quantity: product?.quantity || 1 }

        if (product?.productVariant) {
            updatedProduct.variantId = product.productVariant?.['@id']
        } else {
            updatedProduct.id = product?.product['@id']
        }

        try {
            const data = await basket.updateBasket({
                adminUser: user,
                products: [updatedProduct],
            })
            setOrderData(data)

            toast.success(`Product quantity successfully updated!`)
        } catch (e) {
            if (e?.response) {
                const { data } = e.response
                console.error(e)
                toast.error(
                    data?.hasOwnProperty('hydra:description')
                        ? data['hydra:description']
                        : 'Whoops, there was a problem...'
                )
            }
        }
    }

    const onUpdateBasket = async basketData => {
        try {
            const data = await basket.updateBasket({
                adminUser: user,
                ...basketData,
            })
            setOrderData(data)

            toast.success(`Basket successfully updated!`)
        } catch (e) {
            console.log('error:', e)
            if (e?.response) {
                const { data } = e.response
                console.error(e)
                toast.error(
                    data?.hasOwnProperty('hydra:description')
                        ? data['hydra:description']
                        : 'Whoops, there was a problem...'
                )
            }
        }
    }

    const onRemoveProduct = async product => {
        try {
            const removedProduct = { quantity: 0 }

            if (product?.productVariant?.['@id']) {
                removedProduct.variantId = product.productVariant['@id']
            } else {
                removedProduct.id = product?.product['@id']
            }

            const data = await basket.updateBasket({
                adminUser: user,
                products: [removedProduct],
            })
            setOrderData(data)
        } catch (e) {
            if (e?.response) {
                const { data } = e.response
                console.error(e)
                toast.error(
                    data?.hasOwnProperty('hydra:description')
                        ? data['hydra:description']
                        : 'Whoops, there was a problem...'
                )
            }
        }
    }

    const onPaymentProceed = useCallback(
        async paymentMethod => {
            setBasketErrors([])

            try {
                // Confirm basket
                await axios.put(
                    `${API_URL}/baskets/${orderData.id}/confirm`,
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                )

                //Convert basket
                const {
                    data: { id: convertedBasketId },
                } = await axios.put(
                    `${API_URL}/baskets/${orderData.id}/convert`,
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                )

                ls.set('convertedBasketId', convertedBasketId)

                // Create payment transaction
                const { data: paymentTransactionData } = await axios.post(
                    `${API_URL}/payment-transactions`,
                    {
                        order: `/orders/${convertedBasketId}`,
                        provider: paymentMethod,
                        currency: 'GBP',
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                )

                if (paymentTransactionData?.provider === 'payOnAccount') {
                    ls.remove(`${user?.profile?.id}_basket`)

                    setRedirect('/orders/confirmation')
                } else {
                    if (paymentTransactionData?.paymentGatewayData?.nextUrl) {
                        window.location = paymentTransactionData?.paymentGatewayData?.nextUrl
                    }
                }
            } catch (e) {
                console.error('error:', e)
                if (e?.response?.data?.violations?.length > 0) {
                    // Display errors for invalid
                    const errors = e.response.data.violations.map(error => error.message)
                    setBasketErrors(errors)
                }
            }
        },
        [orderData]
    )

    const fetchShippingServices = async () => {
        if (!basketToken) {
            return
        }
        const { data } = await client(GET_LIST, `baskets/${basketToken}/shipping-services`, {
            id: `baskets/${basketToken}/shipping-services`,
            pagination: { perPage: 10000 },
            filter: {
                status: 'active',
            },
        })

        const services = data.map(service => ({
            label: service.name,
            value: service['@id'],
        }))

        setShippingServiceOptions(services)
    }

    useEffect(() => {
        fetchShippingServices()
    }, [orderData?.deliveryAddress?.postalCode])

    useEffect(() => {
        if (!orders?.createOrders) {
            setRedirect('/')
        }

        if (user?.profile?.id) {
            if (basketToken) {
                setShowConfirmModal(true)
            } else {
                fetchData()
            }
        }
    }, [orders, user?.profile?.id])

    return (
        <OrdersCreateContainer>
            {redirect ? (
                <Redirect to={redirect} />
            ) : (
                <>
                    <Helmet>
                        <title>Create Order | Peracto</title>
                    </Helmet>
                    {isLoading ? (
                        <div style={{ height: '100vh' }}>
                            <div className="!flex !grow !items-center !flex-col !justify-center !h-full">
                                <LoadingIcon />
                            </div>
                        </div>
                    ) : (
                        <div className="row">
                            <div className="col-12 col-lg-8">
                                <Group key="order" id="order" name="Order">
                                    <p className="!font-bold">Order Details</p>

                                    <div className="row">
                                        <div className="col-12 col-lg-5">
                                            <input
                                                type="text"
                                                className="form-control"
                                                onChange={e => setSearchProduct(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.keyCode === 13 || e.which === 13) {
                                                        setShowProductModal(true)
                                                    }
                                                }}
                                                value={searchProduct}
                                                placeholder="Product Name"
                                            />
                                        </div>
                                        <div className="col-12 col-lg-5">
                                            <input
                                                type="text"
                                                className="form-control"
                                                onChange={e => setSearchSku(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.keyCode === 13 || e.which === 13) {
                                                        setShowProductModal(true)
                                                    }
                                                }}
                                                value={searchSku}
                                                placeholder="Product SKU"
                                            />
                                        </div>

                                        <div className="col-12 col-lg-2">
                                            <div className="!flex !justify-end">
                                                <button
                                                    className="btn btn-success"
                                                    onClick={() => setShowProductModal(true)}
                                                >
                                                    Search
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        {orderData?.lines?.length > 0 ? (
                                            <ProductList
                                                includeTax={includeTax}
                                                orderData={orderData}
                                                onRemoveProduct={onRemoveProduct}
                                                onUpdateProduct={onUpdateProduct}
                                            />
                                        ) : (
                                            <p className="!text-center !mt-6 !mb-0">
                                                This order is empty.
                                            </p>
                                        )}
                                    </div>

                                    <hr />

                                    <PromotionCodes
                                        user={user}
                                        basketToken={basketToken}
                                        promotions={orderData?.promotions.filter(
                                            promo => !!promo.promotionCode
                                        )}
                                        setOrderData={setOrderData}
                                    />

                                    <hr />

                                    <table className="table table-sm table-borderless">
                                        <colgroup>
                                            <col width="25%" />
                                            <col width="50%" />
                                            <col width="25%" />
                                        </colgroup>

                                        <tbody>
                                            <tr>
                                                <td className="!font-bold">Subtotal</td>
                                                <td></td>
                                                <td className="!text-right">
                                                    {formatAsCurrency(
                                                        orderData?.subtotal?.excTax,
                                                        orderData?.currency
                                                    )}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td>Shipping</td>
                                                <td>
                                                    <StyledSelect
                                                        options={shippingServiceOptions}
                                                        onChange={e => {
                                                            onUpdateBasket({
                                                                shipping: {
                                                                    service: e.value,
                                                                },
                                                            })
                                                        }}
                                                        disabled={
                                                            !orderData?.deliveryAddress?.postalCode
                                                        }
                                                        defaultValue={
                                                            orderData?.shipping?.service?.name
                                                                ? {
                                                                      label:
                                                                          orderData.shipping.service
                                                                              .name,
                                                                      value:
                                                                          orderData.shipping
                                                                              .service['@id'],
                                                                  }
                                                                : null
                                                        }
                                                        value={
                                                            orderData?.shipping?.service?.name
                                                                ? {
                                                                      label:
                                                                          orderData.shipping.service
                                                                              .name,
                                                                      value:
                                                                          orderData.shipping
                                                                              .service['@id'],
                                                                  }
                                                                : null
                                                        }
                                                    />
                                                </td>
                                                <td className="!text-right">
                                                    {orderData?.shipping?.service?.name ? (
                                                        <>
                                                            {formatAsCurrency(
                                                                orderData?.shipping?.price
                                                                    ?.incTax ||
                                                                    orderData?.shipping
                                                                        ?.priceBeforeDiscount
                                                                        ?.incTax,
                                                                orderData?.currency
                                                            )}
                                                        </>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                            </tr>

                                            {orderData?.discount?.incTax > 0 && (
                                                <tr>
                                                    <td>Total Discount</td>
                                                    <td></td>
                                                    <td className="!text-right">
                                                        -
                                                        {formatAsCurrency(
                                                            includeTax
                                                                ? orderData?.discount?.incTax
                                                                : orderData?.discount?.excTax,
                                                            orderData?.currency
                                                        )}
                                                    </td>
                                                </tr>
                                            )}

                                            {orderData?.promotions?.length > 0 &&
                                                orderData.promotions.map(promo => (
                                                    <tr key={`promotion_${promo.promotion['@id']}`}>
                                                        <td></td>
                                                        <td className="!text-right text-small">
                                                            {promo?.promotionCode && (
                                                                <button
                                                                    className="btn btn-link !p-0 !mr-1 !text-danger"
                                                                    title="Remove this promotion from the order"
                                                                    onClick={async () => {
                                                                        const filteredPromos = orderData?.promotions.filter(
                                                                            orderPromo =>
                                                                                !!orderPromo.promotionCode &&
                                                                                orderPromo.promotionCode !==
                                                                                    promo.promotionCode
                                                                        )
                                                                        const promoCodes = filteredPromos.map(
                                                                            w => w.promotionCode
                                                                        )

                                                                        console.log(
                                                                            'promorCodes',
                                                                            promoCodes
                                                                        )

                                                                        try {
                                                                            const {
                                                                                data,
                                                                            } = await basket.addPromotionCodesToBasket(
                                                                                {
                                                                                    basketId: basketToken,
                                                                                    promoCodes,
                                                                                }
                                                                            )

                                                                            setOrderData(data)

                                                                            toast.success(
                                                                                `Promotion code removed from this order`
                                                                            )
                                                                        } catch (e) {
                                                                            console.error(e)
                                                                            toast.error(
                                                                                e?.error?.body?.hasOwnProperty(
                                                                                    'hydra:description'
                                                                                )
                                                                                    ? e.error.body[
                                                                                          'hydra:description'
                                                                                      ]
                                                                                    : 'Whoops, there was a problem...'
                                                                            )
                                                                        }
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faTimes}
                                                                    />
                                                                </button>
                                                            )}
                                                            {promo.promotion.name}
                                                        </td>
                                                        <td className="!text-right text-small">
                                                            -
                                                            {formatAsCurrency(
                                                                includeTax
                                                                    ? promo.discount?.incTax
                                                                    : promo.discount?.excTax,
                                                                orderData?.currency
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}

                                            {includeTax && (
                                                <tr>
                                                    <td>Tax</td>
                                                    <td></td>
                                                    <td className="!text-right">
                                                        {formatAsCurrency(
                                                            orderData?.total?.incTax -
                                                                orderData?.total?.excTax,
                                                            orderData?.currency
                                                        )}
                                                    </td>
                                                </tr>
                                            )}

                                            <tr className="!border-t !border-t-[#dee2e6]">
                                                <td className="!font-bold">Total</td>
                                                <td></td>
                                                <td className="!text-right !font-bold">
                                                    {formatAsCurrency(
                                                        includeTax
                                                            ? orderData?.total?.incTax
                                                            : orderData?.total?.excTax,
                                                        orderData?.currency
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </Group>

                                <Group key="payment" id="payment" name="Payment">
                                    <p className="!font-bold">Select Payment Method</p>

                                    {paymentMethods.map(method => {
                                        return (
                                            <button
                                                key={method.value}
                                                className="btn btn-outline-primary !mr-2"
                                                onClick={() => {
                                                    onPaymentProceed(method.value)
                                                }}
                                            >
                                                {method.label}
                                            </button>
                                        )
                                    })}

                                    {basketErrors?.length > 0 &&
                                        basketErrors.map((err, idx) => (
                                            <p
                                                key={`error-${idx}`}
                                                className="!mt-4 !mb-0 !text-danger"
                                            >
                                                {err}
                                            </p>
                                        ))}
                                </Group>
                            </div>

                            <div className="col-12 col-lg-4">
                                <Group key="customer" id="customer" name="Customer">
                                    <S.OrderAddress>
                                        {(userData?.firstName || userData?.lastName) && (
                                            <p className="!font-bold">{`${userData?.firstName} ${userData?.lastName}`}</p>
                                        )}
                                        {(orderData?.email || userData?.telephone) && (
                                            <p>
                                                {orderData?.email ? orderData?.email : ''}
                                                {orderData?.email && userData?.telephone && ' / '}
                                                {userData?.telephone ? userData?.telephone : ''}
                                            </p>
                                        )}
                                    </S.OrderAddress>

                                    <button
                                        className="btn btn-link !px-0 text-small"
                                        onClick={() => setCustomerModal(true)}
                                    >
                                        Change Customer
                                    </button>

                                    <CustomerModal
                                        customerModal={customerModal}
                                        setCustomerModal={setCustomerModal}
                                        user={orderData.user}
                                        onUpdateBasket={async basketData => {
                                            await onUpdateBasket(basketData)
                                            await getUserData(basketData)
                                        }}
                                    />

                                    <hr />

                                    <OrderAddress
                                        address={orderData?.deliveryAddress}
                                        title="Shipping Address"
                                    />

                                    {!orderData.deliveryAddress?.postalCode && (
                                        <p className="!text-center text-small text-grey">
                                            No shipping address set for this order
                                        </p>
                                    )}

                                    <button
                                        className="btn btn-link !px-0 text-small"
                                        onClick={() => setAddressModal('delivery')}
                                    >
                                        Change shipping address
                                    </button>

                                    <hr />

                                    <OrderAddress
                                        address={orderData?.billingAddress}
                                        title="Billing Address"
                                    />

                                    {!orderData.billingAddress?.postalCode && (
                                        <p className="!text-center text-small text-grey">
                                            No billing address set for this order
                                        </p>
                                    )}

                                    <button
                                        className="btn btn-link !px-0 text-small"
                                        onClick={() => setAddressModal('billing')}
                                    >
                                        Change billing address
                                    </button>

                                    <AddressModal
                                        addressModal={addressModal}
                                        setAddressModal={setAddressModal}
                                        user={orderData.user}
                                        onUpdateBasket={onUpdateBasket}
                                        addressData={{
                                            billing: orderData.billingAddress,
                                            delivery: orderData.deliveryAddress,
                                        }}
                                    />
                                </Group>
                            </div>
                        </div>
                    )}

                    <OrdersCreateModal
                        onAddProducts={onAddProducts}
                        searchProduct={searchProduct}
                        searchSku={searchSku}
                        setSearchProduct={setSearchProduct}
                        setSearchSku={setSearchSku}
                        showProductModal={showProductModal}
                        setShowProductModal={setShowProductModal}
                    />

                    <Modal
                        isVisible={showConfirmModal}
                        title="Order in Progress"
                        close={() => setShowConfirmModal(false)}
                        buttons={[
                            {
                                type: 'btn-outline-secondary',
                                text: 'Continue Order',
                                action: () => {
                                    fetchData()
                                    setShowConfirmModal(false)
                                },
                            },
                            {
                                type: 'btn-success',
                                text: 'Create New Order',
                                action: () => {
                                    ls.remove(`${user?.profile?.id}_basket`)
                                    fetchData()
                                    setShowConfirmModal(false)
                                },
                            },
                        ]}
                    >
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            size="4x"
                            className="!block !mb-6"
                        />
                        You have an order in progress. Would you like to continue your existing
                        order or create a new one?
                    </Modal>
                </>
            )}
        </OrdersCreateContainer>
    )
}

export default OrdersCreate
